import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Search, ArrowRight, ArrowLeft, RotateCcw, Hash, Lock, Unlock, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { ordemEntradaService } from '../../services/ordemEntradaService';
import { contem } from '../../utils/texto'

const OrdemEntradaManual = ({ leilaoId, podeMoverManual = true, requerJustificativa = false, organizadorId }) => {
  const { leilaoId: urlLeilaoId } = useParams();
  const currentLeilaoId = leilaoId || urlLeilaoId;
  const toast = useToast();
  const queryClient = useQueryClient();

  const [lotesDisponiveis, setLotesDisponiveis] = useState([]);
  const [ordemEntrada, setOrdemEntrada] = useState([]);
  const [ordemModo, setOrdemModo] = useState('auto');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dialog de justificativa
  const [justDialog, setJustDialog] = useState(null) // { pendingResult, posAnt, posNova, loteNumero }
  const [justText, setJustText] = useState('')

  // Edição inline do número da posição (duplo clique no lote)
  const [editandoId, setEditandoId] = useState(null)
  const [editValor, setEditValor] = useState('')
  const cancelouEdicao = useRef(false)

  useEffect(() => {
    if (currentLeilaoId) {
      carregarDados();
    }
  }, [currentLeilaoId]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [ordem, lotes, config] = await Promise.all([
        ordemEntradaService.listarOrdem(currentLeilaoId),
        ordemEntradaService.listarLotesDisponiveis(currentLeilaoId),
        ordemEntradaService.obterConfiguracao(currentLeilaoId)
      ]);
      setOrdemEntrada(ordem);
      setLotesDisponiveis(lotes);
      setOrdemModo(config.ordem_modo || 'auto');
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const reativarOrdemAutomatica = async () => {
    try {
      await ordemEntradaService.resetarModo(currentLeilaoId);
      setOrdemModo('auto');
      toast.success('Ordem automática reativada — novos lotes entrarão automaticamente');
    } catch (error) {
      toast.error('Erro ao reativar ordem automática');
    }
  };

  const lotesFiltrados = lotesDisponiveis.filter(lote =>
    contem(lote.numero, searchTerm.toLowerCase()) ||
    contem(lote.descricao, searchTerm.toLowerCase()) ||
    contem(lote.fazenda_nome, searchTerm.toLowerCase())
  );

  // Mantém a lista do Filtro Automático (react-query) em sincronia com o que foi salvo aqui
  const invalidarOrdemCache = () => queryClient.invalidateQueries(['ordem-entrada', String(currentLeilaoId)]);

  const registrarJustificativa = (loteId, loteNumero, posAnt, posNova, justificativa) => {
    if (!requerJustificativa || !organizadorId || !justificativa) return;
    import('../../services/filtrosBateriasService').then(({ default: svc }) => {
      svc.registrarJustificativa(organizadorId, currentLeilaoId, {
        lote_id: loteId,
        lote_numero: loteNumero,
        posicao_anterior: posAnt,
        posicao_nova: posNova,
        justificativa,
      }).catch(() => {})
    })
  };

  const moverParaOrdem = async (lote, posicao = null, justificativa = null) => {
    // Atualização otimista: move localmente antes de salvar
    setLotesDisponiveis(prev => prev.filter(l => l.id !== lote.id));
    const indice = posicao ? Math.min(posicao, ordemEntrada.length + 1) - 1 : ordemEntrada.length;
    const novoItem = {
      id: `temp-${lote.id}`,
      lote_id: lote.id,
      lote_numero: lote.numero,
      lote_descricao: lote.descricao,
      lote_qtd: lote.qtd,
      fazenda_nome: lote.fazenda_nome,
      raca_nome: lote.raca_nome,
    };
    setOrdemEntrada(prev => {
      const novos = [...prev];
      novos.splice(indice, 0, novoItem);
      return novos.map((item, idx) => ({ ...item, posicao: idx + 1 }));
    });

    try {
      await ordemEntradaService.adicionarLote(currentLeilaoId, lote.id, posicao ? indice + 1 : null, justificativa);
      registrarJustificativa(lote.id, lote.numero, null, indice + 1, justificativa);
      // Recarrega silenciosamente para sincronizar IDs reais
      const [ordem, lotes] = await Promise.all([
        ordemEntradaService.listarOrdem(currentLeilaoId),
        ordemEntradaService.listarLotesDisponiveis(currentLeilaoId),
      ]);
      setOrdemEntrada(ordem);
      setLotesDisponiveis(lotes);
      setOrdemModo('bloqueado');
      invalidarOrdemCache();
      toast.success('Lote adicionado à ordem');
    } catch (error) {
      // Reverte em caso de erro
      await carregarDados();
      toast.error('Erro ao adicionar lote');
    }
  };

  const removerDaOrdem = async (loteId) => {
    // Atualização otimista: remove localmente antes de salvar
    const itemRemovido = ordemEntrada.find(i => i.lote_id === loteId);
    setOrdemEntrada(prev => prev.filter(i => i.lote_id !== loteId).map((item, idx) => ({ ...item, posicao: idx + 1 })));
    if (itemRemovido) {
      setLotesDisponiveis(prev => [...prev, {
        id: itemRemovido.lote_id,
        numero: itemRemovido.lote_numero,
        descricao: itemRemovido.lote_descricao,
        qtd: itemRemovido.lote_qtd,
        fazenda_nome: itemRemovido.fazenda_nome,
        raca_nome: itemRemovido.raca_nome,
      }]);
    }

    try {
      await ordemEntradaService.removerLote(currentLeilaoId, loteId);
      // Recarrega silenciosamente para sincronizar
      const [ordem, lotes] = await Promise.all([
        ordemEntradaService.listarOrdem(currentLeilaoId),
        ordemEntradaService.listarLotesDisponiveis(currentLeilaoId),
      ]);
      setOrdemEntrada(ordem);
      setLotesDisponiveis(lotes);
      setOrdemModo('bloqueado');
      invalidarOrdemCache();
      toast.success('Lote removido da ordem');
    } catch (error) {
      // Reverte em caso de erro
      await carregarDados();
      toast.error('Erro ao remover lote');
    }
  };

  const executarReordenacao = async (updatedItems, posAnt, posNova, loteId, loteNumero, justificativa) => {
    try {
      const ordens = updatedItems.map((item, index) => ({
        lote_id: item.lote_id,
        nova_posicao: index + 1,
      }))
      await ordemEntradaService.reordenarLotes(currentLeilaoId, ordens, loteId, justificativa)
      // Recarrega para exibir na hora a marca "Manual" com quem/quando/motivo
      setOrdemEntrada(await ordemEntradaService.listarOrdem(currentLeilaoId))
      setOrdemModo('bloqueado')
      invalidarOrdemCache()
      registrarJustificativa(loteId, loteNumero, posAnt, posNova, justificativa)
    } catch {
      const ordem = await ordemEntradaService.listarOrdem(currentLeilaoId)
      setOrdemEntrada(ordem)
      toast.error('Erro ao reordenar')
    }
  }

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    if (!podeMoverManual) { toast.error('Você não tem permissão para mover lotes manualmente'); return; }

    const { source, destination } = result;

    // Drag entre listas diferentes (disponíveis <-> ordem)
    if (source.droppableId !== destination.droppableId) {
      if (source.droppableId === 'lotes-disponiveis' && destination.droppableId === 'ordem-entrada') {
        const lote = lotesFiltrados[source.index];
        if (!lote) return;
        // Solta na posição escolhida (e pede justificativa, se exigida)
        const posicao = Math.min(destination.index + 1, ordemEntrada.length + 1);
        if (requerJustificativa) {
          setJustText('')
          setJustDialog({ tipo: 'adicionar', lote, posAnt: null, posNova: posicao, loteNumero: lote.numero })
        } else {
          await moverParaOrdem(lote, posicao);
        }
      } else if (source.droppableId === 'ordem-entrada' && destination.droppableId === 'lotes-disponiveis') {
        const item = ordemEntrada[source.index];
        if (item) await removerDaOrdem(item.lote_id);
      }
      return;
    }

    // Reordenar dentro da mesma lista (apenas ordem-entrada)
    if (source.droppableId === 'ordem-entrada') {
      await moverNaOrdem(source.index, destination.index);
    }
  };

  // Move um lote de `indiceOrigem` para `indiceDestino` (0-based) — usado pelo arrastar e pela edição inline
  const moverNaOrdem = async (indiceOrigem, indiceDestino) => {
    if (indiceOrigem === indiceDestino) return;
    const items = Array.from(ordemEntrada);
    const movedItem = items[indiceOrigem];
    const posAnt = indiceOrigem + 1;
    const posNova = indiceDestino + 1;
    const [reorderedItem] = items.splice(indiceOrigem, 1);
    items.splice(indiceDestino, 0, reorderedItem);
    const updatedItems = items.map((item, index) => ({ ...item, posicao: index + 1 }));
    setOrdemEntrada(updatedItems);

    if (requerJustificativa) {
      setJustText('')
      setJustDialog({ tipo: 'reordenar', updatedItems, posAnt, posNova, loteId: movedItem.lote_id, loteNumero: movedItem.lote_numero })
    } else {
      await executarReordenacao(updatedItems, posAnt, posNova, movedItem.lote_id, movedItem.lote_numero, null)
    }
  };

  const iniciarEdicao = (item) => {
    if (!podeMoverManual) { toast.error('Você não tem permissão para mover lotes manualmente'); return; }
    cancelouEdicao.current = false;
    setEditandoId(item.id);
    setEditValor(String(item.posicao));
  };

  const confirmarEdicao = async (indice) => {
    if (cancelouEdicao.current) { cancelouEdicao.current = false; return; }
    setEditandoId(null);
    const nova = parseInt(editValor, 10);
    if (!Number.isFinite(nova) || nova < 1) { toast.error('Informe uma posição válida'); return; }
    await moverNaOrdem(indice, Math.min(nova, ordemEntrada.length) - 1);
  };

  const confirmarJustificativa = async () => {
    if (!justText.trim() || justText.trim().length < 5) { toast.error('Informe uma justificativa (mínimo 5 caracteres)'); return; }
    const dialog = justDialog
    const justificativa = justText.trim()
    setJustDialog(null)
    setJustText('')
    if (dialog.tipo === 'adicionar') {
      await moverParaOrdem(dialog.lote, dialog.posNova, justificativa)
    } else {
      await executarReordenacao(dialog.updatedItems, dialog.posAnt, dialog.posNova, dialog.loteId, dialog.loteNumero, justificativa)
    }
  }

  const cancelarJustificativa = async () => {
    // Reverter a reordenação (adicionar ainda não alterou nada localmente)
    if (justDialog?.tipo !== 'adicionar') {
      const ordem = await ordemEntradaService.listarOrdem(currentLeilaoId)
      setOrdemEntrada(ordem)
    }
    setJustDialog(null)
    setJustText('')
  }

  const gerarOrdemSequencial = async () => {
    try {
      setSaving(true);
      const todosLotes = [...lotesDisponiveis, ...ordemEntrada.map(item => ({
        id: item.lote_id,
        numero: item.lote_numero
      }))];
      
      const lotesOrdenados = todosLotes.sort((a, b) => {
        const numA = a.numero || '';
        const numB = b.numero || '';
        return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: 'base' });
      });
      
      const lotes_ids = lotesOrdenados.map(lote => lote.id);
      await ordemEntradaService.gerarOrdemSequencial(currentLeilaoId, lotes_ids);
      await carregarDados();
      toast.success(`Sequencial A-Z gerado com ${lotes_ids.length} lotes`);
    } catch (error) {
      toast.error('Erro ao gerar ordem sequencial');
    } finally {
      setSaving(false);
    }
  };

  const gerarOrdemManual = async () => {
    try {
      setSaving(true);
      const result = await ordemEntradaService.gerarOrdemManual(currentLeilaoId);
      await carregarDados();
      toast.success(result.message || 'Ordem manual gerada com sucesso');
    } catch (error) {
      toast.error('Erro ao gerar ordem manual');
    } finally {
      setSaving(false);
    }
  };

  const [confirmRemover, setConfirmRemover] = useState(false);

  const removerOrdem = async () => {
    setConfirmRemover(false);
    try {
      setSaving(true);
      const result = await ordemEntradaService.limparOrdem(currentLeilaoId);
      await carregarDados();
      toast.success(result.message || 'Ordem removida');
    } catch (error) {
      toast.error('Erro ao remover ordem');
    } finally {
      setSaving(false);
    }
  };

  if (!currentLeilaoId) {
    return (
      <div className="text-center py-16">
        <Hash size={48} className="mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Leilão não selecionado</h3>
        <p className="text-gray-600">Selecione um leilão para configurar a ordem de entrada</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Botões de Ação */}
      <div className="mb-6 flex justify-end">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 w-auto">
          <div className="flex gap-4">
            <button
              onClick={gerarOrdemSequencial}
              disabled={saving}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all disabled:opacity-50 font-medium text-base flex items-center justify-center gap-2 py-6 px-8 min-w-[180px]"
            >
              <Hash size={20} />
              {saving ? 'Gerando...' : 'Sequencial A-Z'}
            </button>
            <button
              onClick={gerarOrdemManual}
              disabled={saving}
              className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-lg transition-all disabled:opacity-50 font-medium text-base flex items-center justify-center gap-2 py-6 px-8 min-w-[180px]"
            >
              <ArrowRight size={20} />
              {saving ? 'Gerando...' : 'Ordem do Lote'}
            </button>
            <button
              onClick={() => window.open(`/catalogo-leilao/${currentLeilaoId}`, '_blank')}
              disabled={ordemEntrada.length === 0}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg transition-all disabled:opacity-50 font-medium text-base flex items-center justify-center gap-2 py-6 px-8 min-w-[180px]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Catálogo Leilão (Fazenda)
            </button>
            <button
              onClick={() => setConfirmRemover(true)}
              disabled={saving || ordemEntrada.length === 0}
              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg transition-all disabled:opacity-50 font-medium text-base flex items-center justify-center gap-2 py-6 px-8 min-w-[180px]"
            >
              <Trash2 size={20} />
              Remover Ordem
            </button>
          </div>
        </div>
      </div>

      {/* Banner de ordem bloqueada */}
      {ordemModo === 'bloqueado' && (
        <div className="mb-4 flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3">
          <div className="flex items-center gap-3 text-amber-800">
            <Lock size={18} className="flex-shrink-0" />
            <span className="text-sm font-medium">
              Ordem personalizada — novos lotes cadastrados não entram automaticamente
            </span>
          </div>
          <button
            onClick={reativarOrdemAutomatica}
            className="flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-4 py-1.5 rounded-lg transition-all flex-shrink-0"
          >
            <Unlock size={15} />
            Reativar ordem automática
          </button>
        </div>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Lotes Disponíveis */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 h-fit">
            <div className="p-4 sm:p-6 border-b border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Lotes Disponíveis</h2>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {lotesFiltrados.length}
                </span>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Buscar lotes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            
            <div className="p-4 sm:p-6 max-h-[600px] overflow-y-auto">
              <Droppable droppableId="lotes-disponiveis">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={`space-y-3 min-h-[100px] rounded-xl transition-all ${
                      snapshot.isDraggingOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : ''
                    }`}
                  >
                    {lotesFiltrados.length > 0 ? (
                      lotesFiltrados.map((lote, index) => (
                        <Draggable key={lote.id} draggableId={`disp-${lote.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`group flex items-center justify-between p-4 border rounded-xl transition-all cursor-pointer ${
                                snapshot.isDragging
                                  ? 'border-blue-300 bg-blue-50 shadow-xl scale-105 rotate-1'
                                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                              }`}
                              onClick={() => !snapshot.isDragging && moverParaOrdem(lote)}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0">
                                  {lote.numero}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 truncate">Lote {lote.numero}</div>
                                  <div className="text-sm text-gray-600 truncate">{lote.descricao}</div>
                                  <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                                    <span className="bg-gray-100 px-2 py-0.5 rounded">{lote.qtd} cab</span>
                                    {lote.raca_nome && <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{lote.raca_nome}</span>}
                                    {lote.fazenda_nome && <span className="truncate">{lote.fazenda_nome}</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="p-2 text-blue-600 group-hover:text-blue-800 group-hover:bg-blue-100 rounded-lg transition-all flex-shrink-0">
                                <ArrowRight size={18} />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Search size={24} className="text-gray-400" />
                        </div>
                        <h3 className="font-medium text-gray-900 mb-2">
                          {searchTerm ? 'Nenhum lote encontrado' : 'Todos os lotes na ordem'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {searchTerm ? 'Tente outro termo de busca' : 'Use "Sequencial A-Z" para reorganizar'}
                        </p>
                      </div>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>

          {/* Ordem de Entrada */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 h-fit">
            <div className="p-4 sm:p-6 border-b border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Ordem de Entrada</h2>
                <div className="flex items-center gap-3">
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {ordemEntrada.length} lotes
                  </span>
                  <button
                    onClick={carregarDados}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all"
                    title="Recarregar"
                  >
                    <RotateCcw size={16} />
                  </button>
                </div>
              </div>
              {ordemEntrada.length > 0 && (
                <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                  📝 Arraste os lotes ou dê duplo clique para digitar a posição
                </div>
              )}
            </div>
            
            <div className="p-4 sm:p-6 max-h-[600px] overflow-y-auto">
              <Droppable droppableId="ordem-entrada">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={`space-y-3 min-h-[100px] rounded-xl transition-all ${
                      snapshot.isDraggingOver ? 'bg-green-50 border-2 border-dashed border-green-300' : ''
                    }`}
                  >
                    {ordemEntrada.length > 0 ? (
                      ordemEntrada.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={`ordem-${item.id}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`group flex items-center justify-between p-4 border rounded-xl transition-all cursor-move ${
                                snapshot.isDragging
                                  ? 'border-green-300 bg-green-50 shadow-xl scale-105 rotate-1'
                                  : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                              }`}
                            >
                              <div
                                className="flex items-center gap-4 flex-1 min-w-0"
                                onDoubleClick={() => editandoId !== item.id && iniciarEdicao(item)}
                                title="Duplo clique para digitar a posição"
                              >
                                {editandoId === item.id ? (
                                  <input
                                    type="number"
                                    min={1}
                                    max={ordemEntrada.length}
                                    autoFocus
                                    value={editValor}
                                    onChange={(e) => setEditValor(e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    onBlur={() => confirmarEdicao(index)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.currentTarget.blur();
                                      if (e.key === 'Escape') { cancelouEdicao.current = true; setEditandoId(null); }
                                    }}
                                    className="w-14 h-10 text-center text-sm font-bold rounded-xl border-2 border-green-500 bg-white text-green-800 outline-none flex-shrink-0"
                                  />
                                ) : (
                                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                                    snapshot.isDragging ? 'bg-green-200 text-green-800' : 'bg-green-100 text-green-700'
                                  }`}>
                                    {item.posicao}
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 truncate">
                                    Lote {item.lote_numero}
                                  </div>
                                  <div className="text-sm text-gray-600 truncate">
                                    {item.lote_descricao}
                                  </div>
                                  <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                                    <span className="bg-gray-100 px-2 py-0.5 rounded">{item.lote_qtd} cab</span>
                                    {item.raca_nome && <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{item.raca_nome}</span>}
                                    {item.metodo_gerado === 'MANUAL' ? (
                                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium" title="Posição ajustada manualmente">✋ Manual</span>
                                    ) : item.metodo_gerado === 'FILTRO_BATERIA' ? (
                                      <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium" title="Posição definida pelo filtro automático">⚡ Filtro</span>
                                    ) : item.metodo_gerado === 'FILTRO_NUMERICO' ? (
                                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium" title="Posição definida pelo filtro por números">🔢 Filtro nº</span>
                                    ) : null}
                                    {item.fazenda_nome && <span className="truncate">{item.fazenda_nome}</span>}
                                  </div>
                                  {item.metodo_gerado === 'MANUAL' && (item.movido_por_nome || item.movido_em || item.justificativa) && (
                                    <div className="text-xs text-blue-700 mt-1 truncate" title={item.justificativa || ''}>
                                      {item.movido_por_nome && <span className="font-medium">{item.movido_por_nome}</span>}
                                      {item.movido_em && <span> · {new Date(item.movido_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                                      {item.justificativa && <span className="italic text-gray-600"> · “{item.justificativa}”</span>}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removerDaOrdem(item.lote_id);
                                }}
                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-all opacity-0 group-hover:opacity-100 flex-shrink-0"
                                title="Remover da ordem"
                              >
                                <ArrowLeft size={18} />
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <div className="text-center py-16">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                          <Hash size={32} className="text-blue-600" />
                        </div>
                        <h3 className="font-medium text-gray-900 mb-2">Ordem vazia</h3>
                        <p className="text-sm text-gray-500 mb-4">Adicione lotes clicando neles ou use "Sequencial A-Z"</p>
                        <button
                          onClick={gerarOrdemSequencial}
                          disabled={saving || lotesDisponiveis.length === 0}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all disabled:opacity-50 text-sm font-medium"
                        >
                          Sequencial A-Z
                        </button>
                      </div>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </div>
      </DragDropContext>

      {/* Dialog de Justificativa */}
      {justDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-bold text-gray-900 text-lg mb-2">Justificativa de Movimentação</h3>
            <p className="text-sm text-gray-600 mb-4">
              Lote <strong>{justDialog.loteNumero}</strong>: posição{' '}
              <span className="text-gray-400">{justDialog.posAnt ?? 'fora da ordem'}</span>
              <span className="mx-1.5 text-gray-300">→</span>
              <span className="font-semibold text-gray-800">{justDialog.posNova}</span>
            </p>
            <textarea
              value={justText}
              onChange={e => setJustText(e.target.value)}
              placeholder="Informe o motivo da movimentação manual..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 resize-none focus:ring-2 focus:ring-blue-500 outline-none"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-1">Mínimo 5 caracteres</p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={cancelarJustificativa}
                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium text-sm hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarJustificativa}
                disabled={justText.trim().length < 5}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Messages */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toast.toasts.map((t) => (
          <div
            key={t.id}
            className={`px-6 py-4 rounded-xl shadow-xl text-white font-medium ${
              t.type === 'success' ? 'bg-green-500' :
              t.type === 'error' ? 'bg-red-500' :
              t.type === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>

      {/* Modal Confirmação Remover Ordem */}
      {confirmRemover && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2 text-center">Remover Ordem?</h3>
            <p className="text-gray-600 text-sm text-center mb-5">
              Todos os lotes serão removidos da ordem e voltarão para "Lotes Disponíveis".
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRemover(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={removerOrdem}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-medium text-sm transition"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdemEntradaManual;